import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon, Science as TestIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { broadcastService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const LANGS = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
];
const LANG_LABEL = Object.fromEntries(LANGS.map((l) => [l.code, l.label]));
const SUBSCRIBER_LANG = { 1: 'Duala', 2: 'Français', 3: 'English' };
const MAX_LENGTH = 4000;

const STATUS = {
  sending: { label: 'En cours', color: 'info' },
  done: { label: 'Terminé', color: 'success' },
  cancelled: { label: 'Annulé', color: 'default' },
  interrupted: { label: 'Interrompu', color: 'warning' },
};

const fullName = (s) => `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Sans nom';
const phoneOf = (s) => `${s.country_code}${s.phone_number}`;
const errorMessage = (err, fallback) => (err.response && err.response.data && err.response.data.message) || fallback;

function Broadcast() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [title, setTitle] = useState('');
  const [texts, setTexts] = useState({ fr: '', en: '', es: '', de: '' });
  const [tab, setTab] = useState(0);
  const [languageMode, setLanguageMode] = useState('auto');

  const [recipientMode, setRecipientMode] = useState('all');
  const [subscribers, setSubscribers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState('');

  const [confirm, setConfirm] = useState(null); // aperçu renvoyé par l'API
  const [busy, setBusy] = useState(false);

  // Rythme d'envoi : champs en texte pour laisser l'utilisateur effacer et retaper
  const [pacing, setPacing] = useState({ batchSize: '10', pauseSeconds: '60' });
  const [pacingLimits, setPacingLimits] = useState({ batchSize: [1, 50], pauseSeconds: [10, 600] });
  const [savingPacing, setSavingPacing] = useState(false);

  const [history, setHistory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const pollRef = useRef(null);

  const currentLang = LANGS[tab].code;

  const loadHistory = useCallback(async () => {
    try {
      const res = await broadcastService.getAll();
      setHistory(res.data.broadcasts);
    } catch (err) {
      console.error('Erreur historique envois groupés:', err);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await broadcastService.getById(id);
      setDetail(res.data);
      return res.data;
    } catch (err) {
      console.error('Erreur détail envoi groupé:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') return;
    broadcastService.getRecipients()
      .then((res) => setSubscribers(res.data.subscribers))
      .catch(() => enqueueSnackbar('Erreur lors du chargement des abonnés', { variant: 'error' }));
    broadcastService.getPacing()
      .then((res) => {
        setPacing({ batchSize: String(res.data.batch_size), pauseSeconds: String(res.data.pause_seconds) });
        setPacingLimits(res.data.limits);
      })
      .catch(() => enqueueSnackbar('Erreur lors du chargement du rythme d\'envoi', { variant: 'error' }));
    loadHistory();
  }, [user, enqueueSnackbar, loadHistory]);

  // Suivi de l'avancement toutes les 4 s tant que l'envoi est en cours
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!activeId) return undefined;
    loadDetail(activeId);
    pollRef.current = setInterval(async () => {
      const data = await loadDetail(activeId);
      if (data && data.broadcast.status !== 'sending') {
        clearInterval(pollRef.current);
        loadHistory();
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeId, loadDetail, loadHistory]);

  const filteredSubscribers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subscribers;
    return subscribers.filter((s) => `${fullName(s)} ${phoneOf(s)}`.toLowerCase().includes(q));
  }, [subscribers, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectFiltered = () => setSelected((prev) => new Set([...prev, ...filteredSubscribers.map((s) => s.id)]));
  const clearSelection = () => setSelected(new Set());

  if (!user || user.role !== 'super_admin') {
    return <Alert severity="warning">Cette page est réservée au super admin.</Alert>;
  }

  const payload = () => ({
    title: title.trim(),
    texts,
    language_mode: languageMode,
    recipients: recipientMode === 'all' ? 'all' : [...selected],
  });

  const handleSavePacing = async () => {
    setSavingPacing(true);
    try {
      const res = await broadcastService.setPacing(pacing.batchSize, pacing.pauseSeconds);
      setPacing({ batchSize: String(res.data.batch_size), pauseSeconds: String(res.data.pause_seconds) });
      enqueueSnackbar('Rythme d\'envoi enregistré', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Impossible d\'enregistrer le rythme'), { variant: 'error' });
    } finally {
      setSavingPacing(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      await broadcastService.sendTest(texts, languageMode === 'auto' ? currentLang : languageMode);
      enqueueSnackbar('Test envoyé sur le numéro administrateur', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Échec de l\'envoi du test'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    if (!title.trim()) {
      enqueueSnackbar('Donnez un titre à cet envoi (pour l\'historique)', { variant: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const res = await broadcastService.preview(payload());
      setConfirm(res.data);
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Impossible de préparer l\'envoi'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    setBusy(true);
    try {
      const res = await broadcastService.create(payload());
      setConfirm(null);
      setActiveId(res.data.broadcast_id);
      enqueueSnackbar(`Envoi lancé : ${res.data.to_send} message(s)`, { variant: 'success' });
      loadHistory();
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Échec du lancement'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    try {
      await broadcastService.cancel(activeId);
      enqueueSnackbar('Envoi annulé', { variant: 'info' });
      loadDetail(activeId);
      loadHistory();
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Annulation impossible'), { variant: 'error' });
    }
  };

  const handleResume = async () => {
    try {
      await broadcastService.resume(activeId);
      enqueueSnackbar('Envoi repris', { variant: 'success' });
      setActiveId(null);
      setTimeout(() => setActiveId(activeId), 50); // relance le suivi
      loadHistory();
    } catch (err) {
      enqueueSnackbar(errorMessage(err, 'Reprise impossible'), { variant: 'error' });
    }
  };

  const counts = detail ? detail.counts : null;
  const progress = counts && detail.broadcast.total > 0
    ? Math.round(((counts.sent + counts.failed) / detail.broadcast.total) * 100)
    : 0;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Message à tous</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Envoyez une annonce aux abonnés actifs, tous ou une sélection. Les messages partent un par un,
        avec quelques secondes d'écart, par lots suivis d'une pause (rythme réglable plus bas), pour protéger le numéro WhatsApp.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>1. Le message</Typography>
          <TextField
            label="Titre (usage interne, pour l'historique)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 150 }}
            sx={{ mb: 2 }}
          />
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
            {LANGS.map((l) => (
              <Tab
                key={l.code}
                label={`${l.label}${texts[l.code].trim() ? ' ●' : ''}`}
              />
            ))}
          </Tabs>
          <TextField
            label={`Texte en ${LANGS[tab].label}`}
            value={texts[currentLang]}
            onChange={(e) => setTexts((t) => ({ ...t, [currentLang]: e.target.value }))}
            multiline
            minRows={8}
            fullWidth
            inputProps={{ maxLength: MAX_LENGTH }}
            helperText={`${texts[currentLang].length} / ${MAX_LENGTH} caractères. Laissez vide pour ne pas envoyer dans cette langue.`}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>2. La langue</Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Langue d'envoi</InputLabel>
            <Select value={languageMode} label="Langue d'envoi" onChange={(e) => setLanguageMode(e.target.value)}>
              <MenuItem value="auto">Automatique (langue de chaque abonné)</MenuItem>
              {LANGS.map((l) => (
                <MenuItem key={l.code} value={l.code}>Tous en {l.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            En automatique, les abonnés English reçoivent le texte anglais, les autres (Français, Duala) le texte français.
            Pour l'espagnol ou l'allemand, choisissez « Tous en … » avec une sélection de destinataires.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>3. Les destinataires</Typography>
          <RadioGroup value={recipientMode} onChange={(e) => setRecipientMode(e.target.value)}>
            <FormControlLabel value="all" control={<Radio />} label={`Tous les abonnés actifs (${subscribers.length})`} />
            <FormControlLabel value="selection" control={<Radio />} label={`Une sélection (${selected.size} choisi${selected.size > 1 ? 's' : ''})`} />
          </RadioGroup>

          {recipientMode === 'selection' && (
            <Box sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Rechercher un nom ou un numéro"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <Button size="small" onClick={selectFiltered}>Tout cocher ({filteredSubscribers.length})</Button>
                <Button size="small" onClick={clearSelection}>Tout décocher</Button>
              </Stack>
              <List dense sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {filteredSubscribers.map((s) => (
                  <ListItem key={s.id} disablePadding>
                    <ListItemButton onClick={() => toggle(s.id)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={selected.has(s.id)} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText
                        primary={fullName(s)}
                        secondary={`${phoneOf(s)} · ${SUBSCRIBER_LANG[s.preferred_language_id] || '?'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {filteredSubscribers.length === 0 && (
                  <ListItem><ListItemText secondary="Aucun abonné ne correspond." /></ListItem>
                )}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>4. Le rythme d'envoi</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Les messages partent par lots, avec une pause entre deux lots, pour ménager le numéro WhatsApp.
            Le changement s'applique à partir du prochain envoi.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="Messages par lot"
              type="number"
              size="small"
              value={pacing.batchSize}
              onChange={(e) => setPacing((p) => ({ ...p, batchSize: e.target.value }))}
              inputProps={{ min: pacingLimits.batchSize[0], max: pacingLimits.batchSize[1] }}
              helperText={`entre ${pacingLimits.batchSize[0]} et ${pacingLimits.batchSize[1]}`}
            />
            <TextField
              label="Pause entre deux lots (secondes)"
              type="number"
              size="small"
              value={pacing.pauseSeconds}
              onChange={(e) => setPacing((p) => ({ ...p, pauseSeconds: e.target.value }))}
              inputProps={{ min: pacingLimits.pauseSeconds[0], max: pacingLimits.pauseSeconds[1] }}
              helperText={`entre ${pacingLimits.pauseSeconds[0]} et ${pacingLimits.pauseSeconds[1]}`}
              sx={{ minWidth: 260 }}
            />
            <Button variant="outlined" onClick={handleSavePacing} disabled={savingPacing} sx={{ mt: { sm: 0.25 } }}>
              Enregistrer le rythme
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
        <Button variant="outlined" startIcon={<TestIcon />} onClick={handleTest} disabled={busy}>
          Envoyer un test à mon numéro
        </Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={handlePreview} disabled={busy}>
          Aperçu et envoi
        </Button>
      </Stack>

      {detail && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{detail.broadcast.title}</Typography>
              <Chip size="small" {...(STATUS[detail.broadcast.status] || {})} />
              {detail.broadcast.status === 'sending' && <CircularProgress size={16} />}
            </Stack>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              {counts.sent} envoyé(s) · {counts.failed} échec(s) · {counts.pending} en attente
              {counts.skipped > 0 ? ` · ${counts.skipped} ignoré(s) (pas de texte dans leur langue)` : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
              {detail.broadcast.status === 'sending' && (
                <Button size="small" color="error" onClick={handleCancel}>Annuler l'envoi</Button>
              )}
              {detail.broadcast.status === 'interrupted' && counts.pending > 0 && (
                <Button size="small" variant="contained" onClick={handleResume}>
                  Reprendre ({counts.pending} restant{counts.pending > 1 ? 's' : ''})
                </Button>
              )}
            </Stack>
            {detail.failed.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Échecs</Typography>
                <Table size="small">
                  <TableBody>
                    {detail.failed.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell>{f.name || '—'}</TableCell>
                        <TableCell>{f.phone}</TableCell>
                        <TableCell>{f.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>Historique</Typography>
      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Messages</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell>{new Date(b.created_at).toLocaleString('fr-FR')}</TableCell>
                <TableCell>{b.title}</TableCell>
                <TableCell>{b.total}</TableCell>
                <TableCell><Chip size="small" {...(STATUS[b.status] || {})} /></TableCell>
                <TableCell><Button size="small" onClick={() => setActiveId(b.id)}>Détail</Button></TableCell>
              </TableRow>
            ))}
            {history.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary">Aucun envoi pour l'instant.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmer l'envoi</DialogTitle>
        <DialogContent>
          {confirm && (
            <>
              <Typography sx={{ mb: 1 }}>
                <strong>{confirm.to_send}</strong> message{confirm.to_send > 1 ? 's' : ''} seront envoyés,
                environ {confirm.estimated_minutes} minute{confirm.estimated_minutes > 1 ? 's' : ''},
                par lots de {confirm.batch_size} avec {confirm.batch_pause_seconds} s de pause entre deux lots.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                {Object.entries(confirm.per_language).map(([code, n]) => (
                  <Chip key={code} size="small" label={`${LANG_LABEL[code]} : ${n}`} />
                ))}
              </Stack>
              {confirm.without_text > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {confirm.without_text} destinataire(s) n'ont pas de texte dans leur langue et seront ignorés.
                </Alert>
              )}
              <Alert severity="info">
                Pensez à envoyer d'abord un test à votre numéro. Un message envoyé ne peut pas être rappelé.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Annuler</Button>
          <Button variant="contained" onClick={handleSend} disabled={busy}>Envoyer maintenant</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Broadcast;
